import { supabase } from '../supabase';

export type SedeRestauranteLike = {
  ubicacion_corta?: string;
  direccion_completa?: string;
  horarios?: string;
  ubicacion_gps?: string;
  nombre?: string;
};

export type RestauranteSedesSource = {
  sede_ubicacion_corta?: string | null;
  direccion_completa?: string | null;
  sede_horario?: string | null;
  ubicacion_gps?: string | null;
  sedes?: SedeRestauranteLike[] | null;
};

/** Forma canónica de sede de propuesta derivada del restaurante (índice 0 = principal). */
export type SedePropuestaDesdeRestaurante = {
  sede_index: number;
  sede_nombre: string;
  sede_direccion: string;
  horarios_evento: string;
  ubicacion_gps: string;
  visible_en_ficha: boolean;
};

/**
 * Misma regla que el alta de propuestas: sede principal + sedes adicionales del JSON.
 */
export function buildSedesPropuestaDesdeRestaurante(
  restaurante: RestauranteSedesSource
): SedePropuestaDesdeRestaurante[] {
  const defaultHorario = restaurante.sede_horario || '';
  const sedesFromRest = Array.isArray(restaurante.sedes) ? restaurante.sedes : [];
  const out: SedePropuestaDesdeRestaurante[] = [];

  const tienePrincipal = Boolean(
    (restaurante.sede_ubicacion_corta && String(restaurante.sede_ubicacion_corta).trim()) ||
      (restaurante.direccion_completa && String(restaurante.direccion_completa).trim()) ||
      defaultHorario ||
      (restaurante.ubicacion_gps && String(restaurante.ubicacion_gps).trim())
  );

  if (tienePrincipal) {
    out.push({
      sede_index: 0,
      sede_nombre: (restaurante.sede_ubicacion_corta && String(restaurante.sede_ubicacion_corta).trim()) || 'Sede 1',
      sede_direccion: (restaurante.direccion_completa && String(restaurante.direccion_completa).trim()) || '',
      horarios_evento: defaultHorario,
      ubicacion_gps: (restaurante.ubicacion_gps && String(restaurante.ubicacion_gps).trim()) || '',
      visible_en_ficha: true,
    });
  }

  sedesFromRest.forEach((sede) => {
    out.push({
      sede_index: out.length,
      sede_nombre:
        (sede.ubicacion_corta && String(sede.ubicacion_corta).trim()) ||
        (sede.nombre && String(sede.nombre).trim()) ||
        `Sede ${out.length + 1}`,
      sede_direccion: (sede.direccion_completa && String(sede.direccion_completa).trim()) || '',
      horarios_evento: (sede.horarios && String(sede.horarios)) || defaultHorario,
      ubicacion_gps: (sede.ubicacion_gps && String(sede.ubicacion_gps).trim()) || '',
      visible_en_ficha: true,
    });
  });

  return out;
}

export type SyncSedesResult = {
  propuestasRevisadas: number;
  sedesAgregadas: number;
  sedesActualizadas: number;
};

/**
 * Propaga sedes del restaurante a todas sus propuestas:
 * - agrega índices que faltan (sedes nuevas)
 * - actualiza nombre / dirección / GPS de las existentes
 * - no toca horarios_evento ni visible_en_ficha (ajustes de la propuesta)
 * - no elimina sedes extra que solo existan en la propuesta
 */
export async function syncSedesRestauranteAPropuestas(
  restauranteId: string | number,
  restaurante: RestauranteSedesSource
): Promise<SyncSedesResult> {
  const expected = buildSedesPropuestaDesdeRestaurante(restaurante);
  if (expected.length === 0) {
    return { propuestasRevisadas: 0, sedesAgregadas: 0, sedesActualizadas: 0 };
  }

  const rid = Number(restauranteId);
  if (!Number.isFinite(rid)) {
    return { propuestasRevisadas: 0, sedesAgregadas: 0, sedesActualizadas: 0 };
  }

  const { data: propuestas, error: propError } = await supabase
    .from('propuestas')
    .select('id')
    .eq('restaurante_id', rid);

  if (propError) throw propError;
  if (!propuestas?.length) {
    return { propuestasRevisadas: 0, sedesAgregadas: 0, sedesActualizadas: 0 };
  }

  let sedesAgregadas = 0;
  let sedesActualizadas = 0;

  for (const p of propuestas) {
    const propuestaId = String(p.id);
    const { data: existentes, error: sedesError } = await supabase
      .from('propuestas_sedes')
      .select('id, sede_index, sede_nombre, sede_direccion, ubicacion_gps')
      .eq('propuesta_id', propuestaId);

    if (sedesError) {
      console.warn('[syncSedes] No se pudieron leer sedes de propuesta', propuestaId, sedesError);
      continue;
    }

    const byIndex = new Map<number, (typeof existentes)[number]>();
    for (const s of existentes || []) {
      byIndex.set(Number(s.sede_index), s);
    }

    for (const esperado of expected) {
      const actual = byIndex.get(esperado.sede_index);
      if (!actual) {
        const row: Record<string, unknown> = {
          propuesta_id: propuestaId,
          sede_index: esperado.sede_index,
          sede_nombre: esperado.sede_nombre,
          sede_direccion: esperado.sede_direccion || null,
          horarios_evento: esperado.horarios_evento || '',
          visible_en_ficha: true,
        };
        if (esperado.ubicacion_gps) {
          row.ubicacion_gps = esperado.ubicacion_gps;
        }
        const { error: insertError } = await supabase.from('propuestas_sedes').insert(row);
        if (insertError) {
          console.warn('[syncSedes] Error insertando sede', propuestaId, esperado.sede_index, insertError);
        } else {
          sedesAgregadas += 1;
        }
        continue;
      }

      const nombreActual = String(actual.sede_nombre || '').trim();
      const dirActual = String(actual.sede_direccion || '').trim();
      const gpsActual = String(actual.ubicacion_gps || '').trim();
      const patch: Record<string, unknown> = {};

      if (nombreActual !== esperado.sede_nombre) {
        patch.sede_nombre = esperado.sede_nombre;
      }
      if (dirActual !== (esperado.sede_direccion || '')) {
        patch.sede_direccion = esperado.sede_direccion || null;
      }
      if (gpsActual !== (esperado.ubicacion_gps || '')) {
        // Solo enviar GPS si hay valor o si había valor y ahora se limpia (null).
        patch.ubicacion_gps = esperado.ubicacion_gps || null;
      }

      if (Object.keys(patch).length === 0) continue;

      const { error: updateError } = await supabase
        .from('propuestas_sedes')
        .update(patch)
        .eq('id', actual.id);

      if (updateError) {
        // Si falla por columna GPS inexistente, reintentar sin ese campo.
        if (patch.ubicacion_gps !== undefined && String(updateError.message || '').includes('ubicacion_gps')) {
          delete patch.ubicacion_gps;
          if (Object.keys(patch).length === 0) continue;
          const retry = await supabase.from('propuestas_sedes').update(patch).eq('id', actual.id);
          if (retry.error) {
            console.warn('[syncSedes] Error actualizando sede', propuestaId, esperado.sede_index, retry.error);
          } else {
            sedesActualizadas += 1;
          }
        } else {
          console.warn('[syncSedes] Error actualizando sede', propuestaId, esperado.sede_index, updateError);
        }
      } else {
        sedesActualizadas += 1;
      }
    }
  }

  return {
    propuestasRevisadas: propuestas.length,
    sedesAgregadas,
    sedesActualizadas,
  };
}
